import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Upload, X, Star, Loader2, ArrowLeft, ChevronRight, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import * as Sentry from "@sentry/react";

import { motion } from "framer-motion";
import { ProductFormPreview } from "@/components/app/ProductPreview";

interface UploadedImage {
  id: string;
  name: string;
  isPrimary: boolean;
  tag?: string;
  storagePath: string;
  url: string;
}

interface WizardConfigOption {
  id: string;
  name: string;
  illustration?: string | null;
}



interface ImageSlotDef {
  tag: string;
  label: string;
  description: string;
}

export default function ProductWorkspace() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const { user } = useAuth();

  const isNew = !productId || productId === "new";
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [dbProductId, setDbProductId] = useState<string | null>(isNew ? null : productId!);

  // Config selection
  const [configOptions, setConfigOptions] = useState<WizardConfigOption[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [productConfig, setProductConfig] = useState<any>(null);
  const [configLoading, setConfigLoading] = useState(true);

  // Product name (separate from config-driven fields)
  const [productName, setProductName] = useState("");

  // Form values from the Product JSON form — stored as JSONB
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  // Extract image slots from product config
  const configImageSlots: ImageSlotDef[] = (() => {
    if (!productConfig?.sections) return [];
    const slots: ImageSlotDef[] = [];
    for (const section of productConfig.sections) {
      for (const field of section.fields || []) {
        if (field.type === "image_slot" && field.slots) {
          for (const slot of field.slots) {
            slots.push({ tag: slot.tag, label: slot.label, description: slot.description });
          }
        }
      }
    }
    return slots;
  })();

  // Images
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState<"type" | "details" | "images">(isNew ? "type" : "details");

  // Load available product configs
  useEffect(() => {
    loadConfigOptions();
  }, [workspace]);

  // Load existing product
  useEffect(() => {
    if (!isNew && productId && user) {
      loadProduct(productId);
    }
  }, [productId, user]);

  const loadConfigOptions = async () => {
    if (!workspace) return;
    setConfigLoading(true);

    // Step 1: Get brief config IDs assigned to this workspace
    const { data: wwcData, error: wwcError } = await supabase
      .from("workspace_wizard_configs")
      .select("wizard_config_id")
      .eq("workspace_id", workspace.id);

    if (wwcError) {
      console.error("Failed to load workspace wizard configs:", wwcError);
      Sentry.captureException(wwcError);
      setConfigLoading(false);
      return;
    }

    const briefIds = (wwcData || []).map((r: any) => r.wizard_config_id);
    if (briefIds.length === 0) { setConfigLoading(false); return; }

    // Step 2: Get product config IDs linked to those brief configs
    const { data: links, error: linksError } = await supabase
      .from("config_links")
      .select("linked_config_id")
      .in("brief_config_id", briefIds)
      .eq("linked_type", "product");

    if (linksError) {
      console.error("Failed to load config links:", linksError);
      Sentry.captureException(linksError);
      setConfigLoading(false);
      return;
    }

    const productConfigIds = [...new Set((links || []).map((l: any) => l.linked_config_id))];
    if (productConfigIds.length === 0) { setConfigLoading(false); return; }

    // Step 3: Load those product wizard_configs (incl. config JSON for illustration field)
    const { data: configs, error: configsError } = await supabase
      .from("wizard_configs")
      .select("id, name, config")
      .in("id", productConfigIds);

    if (configsError) {
      console.error("Failed to load product configs:", configsError);
      Sentry.captureException(configsError);
      setConfigLoading(false);
      return;
    }

    if (configs && configs.length > 0) {
      const mapped: WizardConfigOption[] = configs.map((c: any) => ({
        id: c.id,
        name: c.name,
        illustration: (c.config && typeof c.config === "object" ? c.config.illustration : null) ?? null,
      }));
      setConfigOptions(mapped);
      if (mapped.length === 1 && isNew) {
        selectConfig(mapped[0].id);
      }
    }

    setConfigLoading(false);
  };

  const selectConfig = async (configId: string) => {
    setSelectedConfigId(configId);
    const { data } = await supabase
      .from("wizard_configs")
      .select("config")
      .eq("id", configId)
      .single();
    if (data) {
      setProductConfig(data.config);
      setActiveTab("details");
    }
  };

  const loadProduct = async (id: string) => {
    setLoading(true);
    const { data: product } = await supabase
      .from("products")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (!product) {
      navigate("/app/products");
      return;
    }

    setProjectId(product.project_id);
    setDbProductId(product.id);
    setProductName(product.name);

    const params = (product.product_params as Record<string, any>) || {};
    setFormValues(params);

    // Load the config that was used (stored in params._config_id)
    if (params._config_id) {
      setSelectedConfigId(params._config_id);
      const { data: configData } = await supabase
        .from("wizard_configs")
        .select("config")
        .eq("id", params._config_id)
        .single();
      if (configData) {
        setProductConfig(configData.config);
      }
    }

    // Load images
    const { data: imgs } = await supabase
      .from("product_images")
      .select("*")
      .eq("product_id", id);

    if (imgs) {
      const loaded: UploadedImage[] = imgs.map((img) => {
        const { data } = supabase.storage.from("product-images").getPublicUrl(img.storage_path);
        return {
          id: img.id,
          name: img.file_name,
          isPrimary: img.is_primary,
          tag: img.tag || undefined,
          storagePath: img.storage_path,
          url: data.publicUrl,
        };
      });
      setImages(loaded);
    }

    setLoading(false);
    setActiveTab((prev) => (prev === "images" ? "images" : "details"));
  };

  const saveProduct = async (): Promise<string | null> => {
    // Model name is the product name
    const derivedName = formValues.model || "Untitled Product";
    if (!workspace || !user) return null;
    setSaving(true);

    try {
      let currentProjectId = projectId;
      if (!currentProjectId) {
        const { data: project, error } = await supabase
          .from("projects")
          .insert({
            name: derivedName,
            workspace_id: workspace.id,
            user_id: user.id,
          })
          .select()
          .single();
        if (error) throw error;
        currentProjectId = project.id;
        setProjectId(project.id);
      } else {
        await supabase
          .from("projects")
          .update({ name: derivedName })
          .eq("id", currentProjectId);
      }

      const productPayload = {
        name: derivedName,
        product_params: {
          ...formValues,
          _config_id: selectedConfigId,
        },
      };

      if (dbProductId) {
        const { error } = await supabase
          .from("products")
          .update(productPayload)
          .eq("id", dbProductId);
        if (error) throw error;
        setProductName(derivedName);
        return dbProductId;
      } else {
        const { data: product, error } = await supabase
          .from("products")
          .insert({
            ...productPayload,
            project_id: currentProjectId!,
            workspace_id: workspace.id,
            user_id: user.id,
          })
          .select()
          .single();
        if (error) throw error;
        setDbProductId(product.id);
        setProductName(derivedName);
        navigate(`/app/products/${product.id}`, { replace: true });
        return product.id;
      }
    } catch (error: any) {
      console.error("Save product error:", error);
      Sentry.captureException(error);
      toast.error(`Could not save the product: ${error?.message || "unknown error"}`);
      setSaveError(error?.message || "Failed to save product");
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Image handlers
  /**
   * Not memoised on purpose.
   *
   * This was a useCallback keyed on [user, dbProductId, images.length], which
   * captured `saveProduct` — and through it `workspace` — from the render it
   * was created on. On /products/new that first render happens before the
   * workspace context has loaded, so the captured saveProduct saw
   * `workspace === null`, returned null immediately, and the upload aborted
   * before touching storage. No error, no image: uploading a product photo
   * simply did nothing.
   */
  const uploadFiles = async (files: FileList | File[]) => {
      if (!user) return;
      const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
      if (!imageFiles.length) return;

      // Auto-create the product on first upload so the user doesn't need to save manually
      let productIdForUpload = dbProductId;
      if (!productIdForUpload) {
        productIdForUpload = await saveProduct();
        if (!productIdForUpload) return;
      }

      setUploading(true);
      try {
        const uploaded = await Promise.all(
          imageFiles.map(async (file, index) => {
            const ext = file.name.split(".").pop();
            const path = `${user.id}/${productIdForUpload}/${Date.now()}-${Math.random().toString(36).slice(2, 9)}.${ext}`;
            const { error } = await supabase.storage.from("product-images").upload(path, file);
            if (error) throw error;

            const { data: urlData } = supabase.storage.from("product-images").getPublicUrl(path);
            const isPrimary = images.length === 0 && index === 0;

            const { data: dbImg, error: dbErr } = await supabase
              .from("product_images")
              .insert({
                product_id: productIdForUpload,
                user_id: user.id,
                file_name: file.name,
                storage_path: path,
                is_primary: isPrimary,
              })
              .select()
              .single();
            if (dbErr) throw dbErr;

            return {
              id: dbImg.id,
              name: file.name,
              isPrimary: dbImg.is_primary,
              storagePath: path,
              url: urlData.publicUrl,
            };
          })
        );

        setImages((prev) => [...prev, ...uploaded]);
      } catch (err: any) {
        console.error("Upload error:", err);
        Sentry.captureException(err);
        toast.error(`Could not upload the image: ${err?.message || "unknown error"}`);
      } finally {
        setUploading(false);
      }
  };

  const setPrimaryImage = async (id: string) => {
    if (!dbProductId) return;
    await supabase.from("product_images").update({ is_primary: false }).eq("product_id", dbProductId);
    await supabase.from("product_images").update({ is_primary: true }).eq("id", id);
    setImages((prev) => prev.map((img) => ({ ...img, isPrimary: img.id === id })));
  };

  const removeImage = async (id: string) => {
    const img = images.find((i) => i.id === id);
    if (!img) return;
    await supabase.from("product_images").delete().eq("id", id);
    await supabase.storage.from("product-images").remove([img.storagePath]);
    setImages((prev) => {
      const filtered = prev.filter((i) => i.id !== id);
      if (img.isPrimary && filtered.length > 0) {
        filtered[0].isPrimary = true;
        supabase.from("product_images").update({ is_primary: true }).eq("id", filtered[0].id);
      }
      return filtered;
    });
  };

  const setImageTag = async (id: string, tag: string) => {
    const normalizedTag = tag || null;
    await supabase.from("product_images").update({ tag: normalizedTag }).eq("id", id);
    setImages((prev) => prev.map((img) => (img.id === id ? { ...img, tag: normalizedTag || undefined } : img)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/products")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-display text-2xl font-bold">
              {isNew ? (formValues.model || "New Product") : productName || "Product"}
            </h1>
            {selectedConfigId && (
              <span className="text-xs text-muted-foreground">
                {configOptions.find(c => c.id === selectedConfigId)?.name || ""}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saveError && (
            <span className="text-sm text-destructive">{saveError}</span>
          )}
          {productConfig && (
            <Button data-tour="product-editor.save" variant="outline" onClick={() => { setSaveError(null); saveProduct(); }} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          )}
          {dbProductId && (
            <Button
              data-tour="product-editor.continue"
              className="gradient-primary text-primary-foreground"
              onClick={() => navigate(`/app/studio?product=${dbProductId}`)}
            >
              Continue in Studio
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Tab navigation — only show once a product type is chosen */}
      {productConfig && (
        <div data-tour="product-editor.tabs" className="flex gap-1 mb-6 bg-muted/30 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab("details")}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium transition-all",
              activeTab === "details" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab("images")}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium transition-all",
              activeTab === "images" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Images {images.length > 0 && `(${images.length})`}
          </button>
        </div>
      )}

      {/* Type Selection — shown until a product type is chosen */}
      {!productConfig && activeTab === "type" && (
        <div className="glass rounded-xl p-8">
          <h2 className="font-display text-lg font-bold mb-2">What kind of product?</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Pick a product type to get started. We'll show you the right fields to fill in.
          </p>

          {configLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : configOptions.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No product configs assigned to your workspace. Contact your administrator.</p>
            </div>
          ) : (
            <div data-tour="product-editor.type" className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {configOptions.map((config) => {
                const isSelected = selectedConfigId === config.id;
                return (
                  <button
                    key={config.id}
                    onClick={() => selectConfig(config.id)}
                    className={cn(
                      "group relative flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all text-center",
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50 hover:bg-secondary/50"
                    )}
                  >
                    <div className="w-full aspect-square rounded-lg bg-primary/5 overflow-hidden flex items-center justify-center">
                      {config.illustration ? (
                        <img
                          src={config.illustration}
                          alt={config.name}
                          className="w-full h-full object-contain p-2"
                        />
                      ) : (
                        <Package className="w-10 h-10 text-primary/60" />
                      )}
                    </div>
                    <p className="font-semibold text-sm leading-tight">{config.name}</p>
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow">
                        <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Product Details — rendered from config */}
      {activeTab === "details" && productConfig && (
        <div className="glass rounded-xl overflow-hidden">
          <ProductFormPreview
            config={productConfig}
            values={formValues}
            onChange={setFormValues}
          />
        </div>
      )}

      {/* Images tab */}
      {activeTab === "images" && (
        <div className="space-y-6">
          {/* Drop Zone */}
          <label
            className={cn(
              "glass rounded-xl p-10 text-center transition-all duration-300 cursor-pointer block",
              dragOver ? "border-primary bg-primary/5 border-2" : "border-dashed border-2 border-border",
              uploading && "opacity-50 pointer-events-none"
            )}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => e.target.files && uploadFiles(e.target.files)}
              disabled={uploading}
            />
            <div className="w-12 h-12 rounded-lg bg-secondary mx-auto mb-3 flex items-center justify-center">
              {uploading ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /> : <Upload className="w-6 h-6 text-muted-foreground" />}
            </div>
            <p className="font-medium text-sm mb-1">{uploading ? "Uploading..." : "Drag & drop or click to upload"}</p>
            <p className="text-xs text-muted-foreground">Upload all product photos, then tag each one below</p>
          </label>

          {/* Image Grid with JSON-driven tag selector */}
          {images.length > 0 && (
            <div className="glass rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-display font-semibold">Reference Images ({images.length})</h4>
                {configImageSlots.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {images.filter(i => i.tag).length}/{configImageSlots.length} tagged
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {images.map((image) => {
                  const options = configImageSlots.map((slot) => ({ value: slot.tag, label: slot.label }));

                  const currentTagMissing = Boolean(image.tag) && !options.some((option) => option.value === image.tag);

                  return (
                    <div
                      key={image.id}
                      className={cn(
                        "rounded-xl overflow-hidden bg-card border border-border",
                        image.isPrimary && "ring-2 ring-primary"
                      )}
                    >
                      <div className="relative aspect-square bg-secondary p-3">
                        <img src={image.url} alt={image.name} className="absolute inset-0 w-full h-full object-contain p-3" />

                        <div className="absolute top-2 right-2 flex items-center gap-1">
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="w-8 h-8"
                            onClick={() => setPrimaryImage(image.id)}
                          >
                            <Star className={cn("w-4 h-4", image.isPrimary && "fill-primary text-primary")} />
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="w-8 h-8"
                            onClick={() => removeImage(image.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="p-3 bg-card">
                        <select
                          value={image.tag || ""}
                          onChange={(e) => setImageTag(image.id, e.target.value)}
                          className="w-full text-xs bg-secondary px-2 py-1.5 rounded-md border border-border"
                        >
                          <option value="">Select tag...</option>
                          {options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                          {currentTagMissing && image.tag && (
                            <option value={image.tag}>Current tag: {image.tag}</option>
                          )}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
